import { PersonAdd, PersonRemove, PersonGroup } from '@fs/zion-icon'
import { Bleed, colors, DataBlock, Button, LayoutBand, ListItem, MenuOverlay, Paragraph, Separator } from '@fs/zion-ui'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { getCollectionId, useSlsStateContext } from '@fs/zion-sls'
import useRecords from '../../hooks/useRecords'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'

export default function IndexedDataHeader({ additionalHeaderContent }) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { openOverlay } = useOverlayContext()
  const personIdsOrderForImage = zionSlsState.personOrder?.find(
    (po) => po.partitionKey === zionSlsState.currentImageArk
  )

  const { noPeople, isJiapu } = useRecords({
    images: zionSlsState?.images,
    currentImageArk: zionSlsState?.currentImageArk,
    personIdsOrder: personIdsOrderForImage?.personIds,
    thumbnails,
  })
  // Disable edit button for specific collections (e.g., 1931 Canadian Census)
  const disableEditButton = useMemo(() => {
    const image = zionSlsState.images?.find((img) => img.partitionKey === zionSlsState.currentImageArk)
    const collectionId = image && getCollectionId(image)
    return collectionId === '5000196' // Canadian census
  }, [zionSlsState.currentImageArk, zionSlsState.images])

  const handleAddPerson = () => {
    openOverlay('addPersonPage1Generic')
  }

  const handleDeletePerson = () => {
    openOverlay('removePerson')
  }

  const handleEditHouseholds = () => {
    openOverlay('dragNames')
  }

  const shouldShowAddDelete = !disableEditButton && !noPeople && !isJiapu

  return (
    <Bleed top>
      <LayoutBand color={colors.gray02}>
        {additionalHeaderContent && (
          <>
            <Separator size="xs" />
            {additionalHeaderContent}
          </>
        )}
        <Separator size="xs" />
        <Paragraph size="xs">
          {t(
            'select-person.instructions',
            'Notice something wrong? Click on a person to edit their details, or manage households.'
          )}
        </Paragraph>
        <Separator size="xxs" />
        <MenuOverlay
          menuButton={
            <Button emphasis="low" disabled={!shouldShowAddDelete} data-testid="manage-indexes-button">
              {t('select-person.manage-indexes', 'Manage Indexes')}
            </Button>
          }
        >
          <ListItem
            onClick={handleAddPerson}
            primaryText={t('select-person.add-person.button', 'Add Person')}
            startElement={<ListItem.Icon Icon={PersonAdd} />}
            data-testid="add-person-button"
          />
          <ListItem
            onClick={handleDeletePerson}
            primaryText={t('select-person.delete.button', 'Delete Person')}
            startElement={<ListItem.Icon Icon={PersonRemove} />}
            data-testid="delete-person-button"
          />
          <ListItem
            onClick={handleEditHouseholds}
            primaryText={t('select-person.edit-households', 'Edit Households')}
            startElement={<ListItem.Icon Icon={PersonGroup} />}
            data-testid="edit-households-button"
          />
        </MenuOverlay>
        <Separator size="xxs" />
      </LayoutBand>
    </Bleed>
  )
}

export function NamesNotInRecordsHeader() {
  const [t] = useTranslation()
  return (
    <>
      <Separator size="sm" />
      <DataBlock
        heading={t('select-person.title.heading', 'Names not in records')}
        data={t('select-person.title.subheading', 'Add relationships to connect a name to the correct record.')}
      />
      <Separator size="xs" />
    </>
  )
}
